import { Component, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import * as _ from 'lodash';
import { forkJoin, Observable, Subject } from 'rxjs';
import { DialogDateFiltersComponent } from 'src/app/common/dialogs/dialog-date-filters/dialog-date-filters.component';
import { report, reportCategory } from 'src/app/dashboards/models';
import { State } from 'src/app/dashboards/state';
import { deepCheckObject } from 'src/app/dashboards/widget/widget-helpers/helpers';
import { dataSourceType, widgetComponentType, widgetType } from 'src/app/enums';
import { gridItem } from 'src/app/models';
import { ReportsDialogExportComponent } from 'src/app/reports/reports-details/nested-components/reports-dialog-export/reports-dialog-export.component';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { reportSources } from '../../reports-list/reports-list.component';

@Component({
  selector: 'app-base-reports-details',
  templateUrl: './base-reports-details.component.html',
  styleUrls: ['./base-reports-details.component.scss'],
  standalone: false
})

export class BaseReportsDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public reportId: number;
  @Input() public subReportId: number;
  @Input() public fromDashboard: Subject<gridItem | boolean>;
  @Output() public refreshNotifier = new EventEmitter<boolean>();
  @Output() public reportLoadedNotifier = new EventEmitter<boolean>();
  @Output() public reportModifiedNotifier = new EventEmitter<boolean>();
  @Output() public reportExpandedNotifier = new EventEmitter<boolean>();
  @Output() public reportCloseNotifier = new EventEmitter<boolean>();
  @HostBinding('class.expanded-settings') expandedSettingsClass: boolean = false;

  @HostListener('document:keydown.esc', ['$event'])
  onEsc(event: KeyboardEvent) {
    if (this.isReportFullscreen) {
      this.toggleReportFullscreen();
    }
  }

  dataLoaded: boolean = true;
  report: report = null;

  gridItem: gridItem = {
    cols: 6, rows: 6, x: 0, y: 0,
    componentType: widgetComponentType.Reports,
    colorScheme: 2,
    dataSourceType: dataSourceType.Realtime,
    type: widgetType.Table,
    setup: { period: { type: 0 }, sysGroupByTime: ' ', limitRange: false, filters: {} }
  }

  page = { basicExpanded: false, settingsExpanded: false, infoAllColumns: false, reportExpanded: false };

  reportLoaded: boolean = false;
  isData: boolean = false;
  showColumnOrder = false;
  showColumnButton = false;

  limitRange: boolean = false;
  categoriesOpened: boolean = false;
  reportCategories: Array<reportCategory>;

  serviceNumbers: any[] = [];
  sysShowEmpty: boolean;
  sysAgentNoCalls: any;
  sysGroupByTime: any[] = [];
  columns: any[] = [];
  defaultColumns: any[] = [];
  relativeFromCompleted: boolean = false;
  relativeToCompleted: boolean = false;
  canExport: boolean = true;

  agents$: Observable<any[]>;
  userGroups$: Observable<any[]>;
  userGroupsRes: any[] = [];
  queues$: Observable<any[]>;
  terminatingNumbers$: Observable<any[]>;
  menus$: Observable<any[]>;
  accesspoints: any[];

  visualTypeDisabledItems: number[] = [3];
  sysDimsForFilter: string[] = [];

  ngUnsubscribe = new Subject<void>();
  showReport: boolean;
  dataEquality: boolean;
  generateNotifier$ = new Subject<void>();
  orderColumnsNotifier$ = new Subject<void>();
  downloadDialogNotifier$ = new Subject<void>();
  isReportFullscreen: boolean = false;
  isSetupCompleted: boolean = true;
  isFromDashboard: boolean = false;
  initialSetup: any;
  initialGridItemType: widgetType;
  rdp_data = { value: -1, selectedRange: 0 };

  get isReportModified(): boolean {
    let init, setup;
    init = this.initialSetup;
    setup = _.cloneDeep(this.gridItem.setup);

    (this.sysDimsForFilter ?? []).forEach(filter => {
      if (setup[filter]?.length == 0) {
        delete setup[filter];
      }
    });

    setup.period = {};
    setup.limitRange = false;
    setup.filters = {}
    setup.columns?.sort();
    init.columns?.sort();
    return !deepCheckObject(init, setup) || this.initialGridItemType != this.gridItem.type;
  }

  constructor(protected dashboardsService: DashboardService, protected i18nextPipe: I18NextPipe, public dialog: MatDialog, protected store: Store<State>) { }

  ngOnInit(): void {
    this.fromDashboard.subscribe(gi => {
      if (typeof gi === "boolean" && !gi) {
        this.isFromDashboard = false;
      } else {
        this.gridItem = gi as gridItem;
        this.isFromDashboard = true;
      }
    });

    let allCategories: reportCategory = { id: -1, category: "", inUse: 1 };
    this.dashboardsService.getReportCategories().subscribe(rc => this.reportCategories = [...[allCategories], ...rc]);
    this.agents$ = this.dashboardsService.getReportRowsFilter('A', '');
    this.userGroups$ = this.dashboardsService.getReportRowsFilter('G', '');
    this.userGroups$.subscribe(r => {
      let uniqueValueIds = [];
      for (let i = 0; i < r.length; i++) {
        let valueId = r[i]["usergroupId"];
        if (uniqueValueIds.indexOf(valueId) > -1) {

        } else {
          uniqueValueIds.push(valueId)
        }
      }

      for (let i = 0; i < uniqueValueIds.length; i++) {
        let id = uniqueValueIds[i];
        let userGroupsById = r.filter(x => x.usergroupId === id);
        for (let j = 0; j < userGroupsById.length; j++) {
          let userGroup = userGroupsById[j];
          if (j !== 0) {
            userGroup["usergroupId"] = userGroup["usergroupId"] + "_" + j;
          } else {
            userGroup["usergroupId"] = userGroup["usergroupId"].toString();
          }

          this.userGroupsRes.push(userGroup);
        }
      }
    });
    this.queues$ = this.dashboardsService.getReportRowsFilter('Q', '');
    this.terminatingNumbers$ = this.dashboardsService.getReportRowsFilter('T', '');
    this.menus$ = this.dashboardsService.getReportRowsFilter('M', '');
    let dashboardAccess$ = this.dashboardsService.getDashboardPageAccess();
    let userInfo$ = this.dashboardsService.getUserInfo();
    forkJoin([dashboardAccess$, userInfo$]).subscribe(results => {
      let dashboardAccess = results[0];
      let userInfo = results[1];
      this.canExport = dashboardAccess && !userInfo?.userProperties?.isDashboardsReadOnly;
    });

  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reportId || changes.subReportId) {
      this.reportLoaded = false;
      this.loadReportData(changes.reportId?.currentValue || this.reportId, changes.subReportId?.currentValue || this.subReportId);
      this.expandSettings();
    }
  }

  closeSettings() {
    this.reportCloseNotifier.emit(true);
  }

  expandSettings() {
    this.page.reportExpanded = !this.page.reportExpanded;
    this.expandedSettingsClass = this.page.reportExpanded;
    this.reportExpandedNotifier.emit(this.page.reportExpanded);
  }

  toggleReportFullscreen() {
    this.isReportFullscreen = !this.isReportFullscreen;
    // workaround fix for the issue that table is not fully visible on fullscreen mode. Initiated resize event solve the problem with that virtual scroll
    window.dispatchEvent(new Event('resize'));
  }

  reportFullscreen() {
    this.isReportFullscreen = true;
    // workaround fix for the issue that table is not fully visible on fullscreen mode. Initiated resize event solve the problem with that virtual scroll
    window.dispatchEvent(new Event('resize'));
  }

  loadReportData(reportId, subReportId) {
    // virtual method
  }

  setInitialSetup() {
    this.initialSetup = _.cloneDeep(this.gridItem.setup);
    this.initialSetup.period = {};
    this.initialSetup.limitRange = false;
    this.initialSetup.filters = {};
    this.initialGridItemType = this.gridItem.type;
  }

  resetGridItem(reportId, subReportId) {
    this.gridItem = {
      cols: 6, rows: 6, x: 0, y: 0,
      componentType: widgetComponentType.Reports,
      colorScheme: 2,
      dataSourceType: dataSourceType.Realtime,
      type: widgetType.Table,
      setup: {
        period: { type: 0 },
        sysGroupByTime: ' ',
        limitRange: false,
        reportId,
        subReportId,
        filters: {}
      }
    }
  }

  modelChange() {
    let setup = this.gridItem.setup;
    let isCompleted = true;

    var keyColumns = this.columns?.filter(c => c.isKey).map(c => c.labelCode) || [];

    var isValidForChart: boolean = !this.report.disableCharts;
    if (!setup.columns || setup.columns.length > keyColumns.length + 4) isValidForChart = false;
    // var types = [...new Set(setup.columns?.filter(f => keyColumns.indexOf(f) < 0).map(c => this.columns.find(f => f.labelCode == c)?.dbType))] || [];
    const types = [
      ...new Set(
        (setup.columns ?? [])
          .filter(f => keyColumns.indexOf(f) < 0)
          .map(c => this.columns.find(f => f.labelCode === c)?.dbType)
      )
    ];
    if (types.length > 1 || types.indexOf(7) < 0) isValidForChart = false;

    if (setup.columns && !isValidForChart) {
      this.visualTypeDisabledItems = [0, 1, 2, 3];
    } else {
      this.visualTypeDisabledItems = [3];
    }

    if (this.columns?.length > 0 && this.visualTypeDisabledItems.indexOf(this.gridItem.type) > -1) {
      this.gridItem.type = widgetType.Table;
    }

    if (this.serviceNumbers?.length > 0 && !setup.serviceNumber) isCompleted = false;
    if (!setup.columns || setup.columns.length < keyColumns.length + 1) isCompleted = false;

    if (setup.period?.type == 0 && (!setup.period.from || !setup.period.to)) isCompleted = false;
    this.isSetupCompleted = isCompleted;
    this.notifyReportModified();
  }

  notifyReportModified() {
    this.reportModifiedNotifier.emit(this.isReportModified);
  }

  selectModelChange($event, modelName) {
    this.gridItem.setup[modelName] = $event;
    this.modelChange();
  }


  showLimitTimeRange() {
    const dialogRef = this.dialog.open(DialogDateFiltersComponent, {
      data: { gridItem: this.gridItem },
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      width: "400px",
      height: "640px",
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.gridItem.setup.filters = Object.assign({}, data.filters);
      }
    });
  }

  isTimeRangeVisible(): boolean {
    let hiddenReports = ["AgentIdIO", "MaxMinAvg", "AgentPause"];
    if (hiddenReports.indexOf(this.report["report_Code"]) > -1) return false;
    return true;
  }

  setPeriod($event) {
    this.gridItem.setup.period = $event;
  }

  openDownloadDialog() {
    this.downloadDialogNotifier$.next();
  }

  dataReceived(isData: boolean) {
    this.showColumnButton = isData;
    this.isData = isData;
  }

  //filtersLengthIsEven() {
  //  if (this.report["report_Code"] === "AgentGroup") return true;
  //  if (this.report["report_Code"] === "AgentQ") return true;
  //  if (this.report["report_Code"] === "AgentSMS") return false;
  //  if (this.report["report_Code"] === "MaxMinAvg") return false;
  //  return this.sysDimsForFilter.length % 2 === 0 ? true : false;
  //}

  exportReport() {
    let gridItemDeepCopy: gridItem = <gridItem>JSON.parse(JSON.stringify(this.gridItem));
    gridItemDeepCopy.setup.period.from = new Date(gridItemDeepCopy.setup.period.from);
    gridItemDeepCopy.setup.period.to = new Date(gridItemDeepCopy.setup.period.to);
    gridItemDeepCopy.componentType = this.report.report_Source == reportSources.PCC ? widgetComponentType.Reports : widgetComponentType.RuleMatch;

    let dialogRef = this.dialog.open(ReportsDialogExportComponent, {
      data: { gridItem: gridItemDeepCopy, report: this.report, isReportModified: this.isReportModified },
      panelClass: ['dialog-standard-pannel', 'child-dropdown-friendly'],
      width: "460px",
      height: "650px"
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.refreshNotifier.emit(true);
    });
  }

  columnOrderEvent(columns: string[]) {
    this.gridItem.setup.columns = columns;
    this.orderColumnsNotifier$.next();
  }

  saveAsDilogClosedHandler(result) {
    if (result) {
      this.refreshNotifier.emit(true);
      this.setInitialSetup();
      this.notifyReportModified();
    }
  }

  reportSavedHandler() {
    this.setInitialSetup();
    this.notifyReportModified();
  }
}
